/**
 * 💰 useCurrentMonthBalance - Hook pentru Global Pocket Money Indicator
 * 
 * FEATURES:
 * ✅ Calculează SOLDUL TOTAL DISPONIBIL: SUM(ALL income completed) - SUM(ALL expense+saving completed)
 * ✅ Arată câți bani ai efectiv disponibili în momentul de față (ALL TIME)
 * ✅ TanStack Query pentru caching și real-time updates
 * ✅ Optimizat pentru performance cu proper cache invalidation
 * ✅ React 19 clean patterns
 * 
 * USAGE:
 * const { balance, isLoading, error } = useCurrentMonthBalance();
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { TransactionType } from '@buzunarul/shared-constants';
import { format } from 'date-fns';

interface MonthlyBalanceData {
  balance: number;
  totalIncome: number;
  totalExpense: number;
  totalSaving: number;
  month: string;
}

interface UseCurrentMonthBalanceReturn {
  balance: number;
  breakdown: MonthlyBalanceData;
  isLoading: boolean;
  error: Error | null;
}

const fetchCurrentMonthBalance = async (): Promise<MonthlyBalanceData> => {
  const currentDate = new Date();
  const currentMonth = format(currentDate, 'yyyy-MM');
  
  console.log('🏦 [useCurrentMonthBalance] Fetching TOTAL AVAILABLE BALANCE (all time)');

  // TEMPORAR: Verificăm ce execution_status avem în DB
  const { data: allTx, error: allError } = await supabase
    .from('transactions')
    .select('execution_status')
    .limit(100);
  
  if (allTx) {
    const statusCounts = allTx.reduce((acc, tx) => {
      const status = tx.execution_status || 'NULL';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log('📊 Execution Status Distribution:', statusCounts);
  }

  // Fetch ALL COMPLETED transactions from ALL TIME pentru sold total disponibil
  const { data: transactions, error } = await supabase
    .from('transactions')
    .select('type, actual_amount, amount, execution_status, date')
    .or('execution_status.eq.COMPLETED,execution_status.eq.MODIFIED,execution_status.is.null') // Include NULL for old data
    .order('date', { ascending: false }); // Latest first for debugging

  if (error) {
    console.error('❌ [useCurrentMonthBalance] Database error:', error);
    throw new Error(`Failed to fetch monthly balance: ${error.message}`);
  }

  console.log(`📊 [useCurrentMonthBalance] Found ${transactions.length} completed transactions ALL TIME`);
  
  // Debug: show last few transactions
  if (transactions.length > 0) {
    console.log('🔍 Last 5 transactions:', transactions.slice(0, 5).map(t => ({
      date: t.date,
      type: t.type,
      actual_amount: t.actual_amount,
      status: t.execution_status
    })));
  }

  // Calculate totals with proper income/expense separation
  let totalIncome = 0;
  let totalExpense = 0;
  let totalSaving = 0;

  transactions.forEach(tx => {
    // Tratăm NULL ca COMPLETED pentru backward compatibility
    const isCompleted = !tx.execution_status || tx.execution_status === 'COMPLETED';
    const isModified = tx.execution_status === 'MODIFIED';
    
    // Pentru MODIFIED: folosește actual_amount, pentru COMPLETED/NULL: folosește amount
    const amount = isModified && tx.actual_amount !== null 
      ? Number(tx.actual_amount) 
      : Number(tx.amount || 0);
    
    console.log(`📊 Processing: ${tx.type} - ${amount} RON (${tx.execution_status || 'NULL'}) - ${tx.date}`);
    
    if (amount <= 0) {
      console.log('⚠️ Skipping zero/negative amount');
      return;
    }
    
    switch (tx.type) {
      case TransactionType.INCOME:
        totalIncome += amount;
        break;
      case TransactionType.EXPENSE:
        totalExpense += amount;
        break;
      case TransactionType.SAVING:
        totalSaving += amount;
        break;
      default:
        console.warn(`⚠️ [useCurrentMonthBalance] Unknown transaction type: ${tx.type}`);
    }
  });

  // Calculate final balance: Income - (Expenses + Savings) = SOLD DISPONIBIL TOTAL
  const balance = totalIncome - (totalExpense + totalSaving);
  
  const result: MonthlyBalanceData = {
    balance,
    totalIncome,
    totalExpense,
    totalSaving,
    month: currentMonth // Keep for display, dar datele sunt ALL TIME
  };

  console.log('✅ [useCurrentMonthBalance] SOLD TOTAL DISPONIBIL:', {
    totalIncome: `+${totalIncome} RON`,
    totalExpense: `-${totalExpense} RON`,
    totalSaving: `-${totalSaving} RON`,
    balance: `${balance} RON (sold disponibil)`
  });
  
  return result;
};

export const useCurrentMonthBalance = (): UseCurrentMonthBalanceReturn => {
  const currentMonth = format(new Date(), 'yyyy-MM');
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['totalAvailableBalance'], // Schimbat key - nu mai e monthly, e TOTAL
    queryFn: fetchCurrentMonthBalance,
    staleTime: 30 * 1000, // 30 seconds - balance se poate schimba când completezi tranzacții
    gcTime: 5 * 60 * 1000, // 5 minutes - keep în cache pentru performance
    refetchOnWindowFocus: true, // Refetch când revii în app - important pentru sold real-time
    retry: 2, // Retry failed requests 2 times
  });

  return {
    balance: data?.balance || 0,
    breakdown: data || {
      balance: 0,
      totalIncome: 0,
      totalExpense: 0,
      totalSaving: 0,
      month: currentMonth
    },
    isLoading,
    error: error as Error | null
  };
};